import { TextInput, useForm } from '@faceup/form'
import { UntitledIcon } from '@faceup/icons'
import { ulMail01 } from '@faceup/icons/ulMail01'
import { ulRefreshCw02 } from '@faceup/icons/ulRefreshCw02'
import { ulTrash01 } from '@faceup/icons/ulTrash01'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Form } from '@faceup/ui'
import { Alert, Button, Popconfirm, Tag, Typography, useMessage } from '@faceup/ui-base'
import {
  isCompanyEmail as isCompanyEmailFn,
  ResendVictimEmailVerificationResult,
} from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useEffect, useState } from 'react'
import { sharedMessages } from '../Shared/translations'

const messages = defineMessages('followUp.components.emailSubscriptionForm', [
  { id: 'title', defaultMessage: '' },
  { id: 'label', defaultMessage: '' },
  { id: 'help', defaultMessage: '' },
  { id: 'subscribedDescription', defaultMessage: '' },
  { id: 'unsubscribe', defaultMessage: '' },
  { id: 'emailPlaceholder', defaultMessage: '' },
  { id: 'saveCompanyEmail', defaultMessage: '' },
  { id: 'verificationPendingAlert', defaultMessage: '', values: { email: true } },
  { id: 'resendVerification', defaultMessage: '' },
  { id: 'changeEmail', defaultMessage: '' },
  { id: 'resendSuccess', defaultMessage: '' },
  { id: 'resendRateLimited', defaultMessage: '', values: { seconds: true } },
  { id: 'resendAlreadyVerified', defaultMessage: '' },
  { id: 'resendError', defaultMessage: '' },
] as const)

const schema = yup.object().shape({
  email: yup.string().email().required(),
})

export type ResendOutcome = {
  result: ResendVictimEmailVerificationResult
  retryAfterSeconds: number | null
}

type EmailSubscriptionFormProps = {
  maskedEmail: string | null
  isEmailVerified?: boolean
  onSubmit: (email: string) => Promise<boolean>
  onUnsubscribe?: () => Promise<boolean>
  onResend?: () => Promise<ResendOutcome>
}

export const EmailSubscriptionForm = ({
  maskedEmail,
  isEmailVerified,
  onSubmit,
  onUnsubscribe,
  onResend,
}: EmailSubscriptionFormProps) => {
  const [isCompanyEmail, setIsCompanyEmail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isChangingEmail, setIsChangingEmail] = useState(false)
  const { formatMessage } = useIntl()
  const message = useMessage()

  const form = useForm({
    schema,
    defaultValues: { email: '' },
    afterSubmit: 'persistValues',
  })

  const emailValue = form.watch('email')
  const isValid = form.formState.isValid

  // biome-ignore lint/correctness/useExhaustiveDependencies(form.reset): form.reset changes each render and would cause a loop
  useEffect(() => {
    if (!maskedEmail) {
      form.reset({ email: '' })
    }
  }, [maskedEmail])

  useEffect(() => {
    const checkIsCompanyEmail = async () => setIsCompanyEmail(await isCompanyEmailFn(emailValue))
    void checkIsCompanyEmail()
  }, [emailValue])

  const handleSubmit = async () => {
    const emailToSave = form.getValues('email')
    setIsSubmitting(true)
    try {
      const success = await onSubmit(emailToSave)
      if (success) {
        setIsChangingEmail(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUnsubscribe = async () => {
    if (!onUnsubscribe) {
      return
    }
    setIsSubmitting(true)
    try {
      await onUnsubscribe()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!onResend) {
      return
    }
    setIsResending(true)
    try {
      const { result, retryAfterSeconds } = await onResend()
      switch (result) {
        case ResendVictimEmailVerificationResult.Success:
          message.success(formatMessage(messages.resendSuccess))
          break
        case ResendVictimEmailVerificationResult.RateLimited:
          message.error(
            formatMessage(messages.resendRateLimited, {
              values: { seconds: retryAfterSeconds ?? 0 },
            })
          )
          break
        case ResendVictimEmailVerificationResult.AlreadyVerified:
          message.error(formatMessage(messages.resendAlreadyVerified))
          break
        case ResendVictimEmailVerificationResult.NoEmailSet:
          message.error(formatMessage(messages.resendError))
          break
      }
    } catch {
      message.error(formatMessage(messages.resendError))
    } finally {
      setIsResending(false)
    }
  }

  if (maskedEmail && !isChangingEmail) {
    return (
      <div className='flex flex-col gap-4 w-full'>
        <Typography.Title level={4} className='!m-0'>
          <FormattedMessage message={messages.title} />
        </Typography.Title>
        {isEmailVerified === false ? (
          <>
            <Alert
              type='warning'
              message={
                <FormattedMessage
                  message={messages.verificationPendingAlert}
                  values={{ email: maskedEmail }}
                />
              }
            />
            <div className='flex gap-3'>
              {onResend && (
                <Button
                  type='primary'
                  size='small'
                  icon={<UntitledIcon icon={ulRefreshCw02} />}
                  loading={isResending}
                  onClick={() => handleResend()}
                >
                  <FormattedMessage message={messages.resendVerification} />
                </Button>
              )}
              <Button size='small' onClick={() => setIsChangingEmail(true)}>
                <FormattedMessage message={messages.changeEmail} />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className='flex items-center gap-2'>
              <Tag color='success' bordered icon={<UntitledIcon icon={ulMail01} size={12} />}>
                {maskedEmail}
              </Tag>
            </div>
            <Typography.Text>
              <FormattedMessage message={messages.subscribedDescription} />
            </Typography.Text>
            {onUnsubscribe && (
              <div>
                <Button
                  type='primary'
                  size='small'
                  icon={<UntitledIcon icon={ulTrash01} />}
                  loading={isSubmitting}
                  onClick={() => handleUnsubscribe()}
                >
                  <FormattedMessage message={messages.unsubscribe} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  const saveButton = isCompanyEmail ? (
    <Popconfirm title={formatMessage(messages.saveCompanyEmail)} onConfirm={() => handleSubmit()}>
      <Button
        type='primary'
        data-test='save-email'
        htmlType='submit'
        loading={isSubmitting}
        disabled={!isValid}
      >
        <FormattedMessage message={sharedMessages.save} />
      </Button>
    </Popconfirm>
  ) : (
    <Button
      type='primary'
      data-test='save-email'
      htmlType='submit'
      loading={isSubmitting}
      disabled={!isValid}
      onClick={() => handleSubmit()}
    >
      <FormattedMessage message={sharedMessages.save} />
    </Button>
  )

  return (
    <div className='flex flex-col gap-4 w-full'>
      <Typography.Title level={4} className='!m-0'>
        <FormattedMessage message={messages.title} />
      </Typography.Title>
      <Form
        onSubmit={() => {
          if (isCompanyEmail) {
            return
          }
          void handleSubmit()
        }}
      >
        <div className='flex flex-col sm:flex-row gap-2 sm:items-end'>
          <div className='flex-1'>
            <TextInput
              withAsterisk={false}
              control={form.control}
              autoComplete='email'
              name='email'
              type='email'
              label={<FormattedMessage message={messages.label} />}
              placeholder={formatMessage(messages.emailPlaceholder)}
            />
          </div>
          {saveButton}
        </div>
      </Form>
    </div>
  )
}
